import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import Actions from '../../../Flux/index.js'
import type { IOrder } from '../../../Model/index.js'
import { orderSlice } from '../../../Flux/Slice/Order/OrderSlice.js'
import type { TaxaOption } from '../HomePage/01-CreateOrderPage/TaxaOptions.js'
import { taxaOptions } from '../HomePage/01-CreateOrderPage/TaxaOptions.js'
import type { RedlistModeKey } from '../../../Generated/RedlistMode.js'
import { RedlistModes } from '../../../Generated/RedlistMode.js'
import type { ProcessingTypeKey } from '../../../Generated/ProcessingType.ts'
import { ProcessingTypes } from '../../../Generated/ProcessingType.ts'
import type { SampleConfigurationTypeKey } from '../../../Generated/SampleConfigurationType.js'
import { SampleConfigurationTypes } from '../../../Generated/SampleConfigurationType.js'
import { FormControl, Label } from '../../Common/index.js'
import { TooltipTrigger } from '../../../Provider/TooltipManager.js'
import type { BaseModalProps } from '../../Modal/BaseModal.js'
import { BaseModal } from '../../Modal/BaseModal.js'
import { TargetRegions } from '../../../Generated/TargetRegion.ts'
import {
  getAllowedTargetTaxaForProcessingType,
  getProcessingTypeTaxaValidationMessage,
  isProcessingTypeTaxaCombinationValid
} from '../../../Util/processingType.ts'
import {
  getAllowedSampleConfigurationsForPlatform,
  getDetectedSequencingPlatform,
  getEffectiveSequencingPlatform,
  getMergedSampleConfigurationForPrimersRemoved,
  describeSampleConfiguration,
  getSampleConfigurationLabel,
  getUnsupportedProcessingTypeMessage,
  isPrimersRemovedSampleConfiguration,
  isProcessingTypeAllowedForPlatform,
  usesPrimersRemovedToggle
} from '../../../Util/sequencingPlatform.ts'
import { useGetSamplePoolSelector } from '../../../Flux/Selector/index.js'
import { SequencingPlatformSettingRow } from '../../Component/SequencingPlatformSettingRow.tsx'
import { addMessage } from '../../../Flux/Slice/Application/ApplicationSlice.js'
import { infoMessage } from '../../../Model/Message/IMessage.js'

const SWITCHABLE_CONFIGURATIONS: SampleConfigurationTypeKey[] = [
  'SINGLE_SAMPLE_PAIRS',
  'TRIMMED_SAMPLES',
  'MERGED_SAMPLES',
  'MERGED_AND_TRIMMED_SAMPLES'
]

export interface EditOrderModalProps {
  order: IOrder
}

export const EditOrderModal = ({ isOpen, onClose, order }: EditOrderModalProps & BaseModalProps) => {
  const dispatch = useDispatch()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [allowSingletons, setAllowSingletons] = useState<boolean>(false)
  const [selectedTaxa, setSelectedTaxa] = useState<TaxaOption>(taxaOptions[0])
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null)
  const [processingType, setProcessingType] = useState<ProcessingTypeKey>('METABARCODING')
  const [redlistMode, setRedlistMode] = useState<RedlistModeKey>('NONE')
  const [sampleConfiguration, setSampleConfiguration] = useState<SampleConfigurationTypeKey>('SINGLE_SAMPLE_PAIRS')

  const samplePools = useGetSamplePoolSelector()

  // Preprocessing starts on submission (see Util/OrderState.ts) and immediately picks the DAG chain and
  // writes intermediates to EFS, so the sample format freezes there — from then on, fixing it is a re-run,
  // not a dropdown.
  const preprocessingStarted = !!order.orderSubmission

  const detectedSequencingPlatform = getDetectedSequencingPlatform(order, samplePools)
  const effectiveSequencingPlatform = getEffectiveSequencingPlatform(detectedSequencingPlatform, order.sequencingPlatformOverride)
  const allowedSampleConfigurations = getAllowedSampleConfigurationsForPlatform(effectiveSequencingPlatform)

  // Mirrors OrderServiceImpl.resolveSampleConfigurationSwitch: multi-sample pairs never switch, merged to paired
  // always does (each sample then waits for its R2), paired to merged only while no sample is an R1/R2 pair.
  const switchable = order.sampleConfiguration !== 'COMBINED_SAMPLE_PAIRS'
  const sampleFormatOptions = allowedSampleConfigurations ?? (switchable ? SWITCHABLE_CONFIGURATIONS : [order.sampleConfiguration])
  const canSwitchSampleConfiguration = (!!allowedSampleConfigurations || switchable) && !preprocessingStarted
  const items = samplePools === 'LOADING' ? [] : samplePools.flatMap((pool) => [...pool.samples, ...pool.negativeControls])
  const pairedItemCount = items.filter((item) => !!item.reverseFileId).length
  const currentIsMerged = SampleConfigurationTypes[order.sampleConfiguration].impliesMerged
  const blockedByPairs = (key: SampleConfigurationTypeKey) =>
    key !== order.sampleConfiguration && !currentIsMerged && SampleConfigurationTypes[key].impliesMerged && pairedItemCount > 0
  const leavesR2Missing = currentIsMerged && !SampleConfigurationTypes[sampleConfiguration].impliesMerged && items.length > 0

  const isPrimersRemovedToggle = usesPrimersRemovedToggle(effectiveSequencingPlatform)

  const platformProcessingTypeError = getUnsupportedProcessingTypeMessage(effectiveSequencingPlatform, processingType)

  useEffect(() => {
    if (isOpen && order) {
      setName(order.name)
      setDescription(order.description ?? '')
      setAllowSingletons(order.allowSingletons)
      setSelectedTaxa(taxaOptions.find((x) => x.value === order.targetTaxonomy) ?? taxaOptions[0])
      setSelectedRegion(order.targetRegion ?? null)
      setProcessingType(order.processingType ?? 'METABARCODING')
      setRedlistMode(order.redlistMode ?? 'NONE')
      setSampleConfiguration(order.sampleConfiguration)
    }
  }, [isOpen, order])

  useEffect(() => {
    const allowedTaxa = getAllowedTargetTaxaForProcessingType(processingType)
    if (allowedTaxa === null || allowedTaxa.includes(selectedTaxa?.value ?? 'OTHER')) {
      return
    }
    const fallbackTaxa = taxaOptions.find((option) => allowedTaxa.includes(option.value))
    if (fallbackTaxa) {
      setSelectedTaxa(fallbackTaxa)
    }
  }, [processingType, selectedTaxa])

  // The platform can rule out the order's current sample format (e.g. ONT samples on an order still set to
  // SINGLE_SAMPLE_PAIRS) — fall back to the first allowed option rather than leaving the dropdown pointing
  // at a value it does not offer.
  useEffect(() => {
    if (!allowedSampleConfigurations || allowedSampleConfigurations.includes(sampleConfiguration)) {
      return
    }
    setSampleConfiguration(allowedSampleConfigurations[0])
  }, [allowedSampleConfigurations, sampleConfiguration])

  const handleSubmit = () => {
    // Form validation
    if (!name.trim()) {
      console.log('no name')
      // Name is required
      return
    }

    if (!isProcessingTypeTaxaCombinationValid(processingType, selectedTaxa.value)) {
      dispatch(addMessage(infoMessage(getProcessingTypeTaxaValidationMessage(processingType))))
      return
    }

    if (platformProcessingTypeError) {
      dispatch(addMessage(infoMessage(platformProcessingTypeError)))
      return
    }

    dispatch(
      Actions[orderSlice.name].updateOrderNameRequest({
        name: name,
        description: description.trim() || null,
        orderId: order.id,
        version: order.version || '0',
        allowSingletons: allowSingletons,
        taxa: selectedTaxa.value,
        targetRegion: selectedTaxa.value === 'CO1_ANIMAL' ? (selectedRegion ?? undefined) : undefined,
        processingType,
        redlistMode: redlistMode,
        sampleConfiguration: sampleConfiguration
      })
    )
    onClose()
  }

  const resetForm = () => {
    setName(order.name)
    setDescription(order.description ?? '')
    setAllowSingletons(order.allowSingletons)
    setSelectedTaxa(taxaOptions.find((x) => x.value === order.targetTaxonomy) ?? taxaOptions[0])
    setSelectedRegion(order.targetRegion ?? null)
    setProcessingType(order.processingType ?? 'METABARCODING')
    setRedlistMode(order.redlistMode ?? 'NONE')
    setSampleConfiguration(order.sampleConfiguration)
  }

  return (
    <BaseModal isOpen={isOpen} onClose={onClose}>
      <div className="modal-box">
        <h3 className="text-lg font-bold">Edit Order</h3>
        <div className="form-control w-full">
          <label className="label">
            <span className="label-text">Order Name</span>
          </label>
          <input
            type="text"
            placeholder="Enter Order name"
            className="input input-bordered w-full"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="form-control w-full">
          <label className="label">
            <span className="label-text">Description</span>
          </label>
          <textarea
            placeholder="Enter description"
            className="textarea textarea-bordered w-full min-h-24"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <SequencingPlatformSettingRow detected={detectedSequencingPlatform} override={order.sequencingPlatformOverride} />

        <FormControl>
          <TooltipTrigger
            tooltip={
              isPrimersRemovedToggle
                ? 'Whether the primers were already removed from these Nanopore reads before upload. This is only possible before the order is submitted.'
                : 'Switch the sample format. This re-labels every sample in the order and is only possible before the order is submitted. Going from merged to paired-end leaves each sample waiting for its R2 file; going from paired-end to merged is only possible while no sample is an R1/R2 pair.'
            }
            withIcon
          >
            <Label inline valid={true}>
              {isPrimersRemovedToggle ? 'Primers already removed?' : 'Sample Format'}
            </Label>
          </TooltipTrigger>
          {isPrimersRemovedToggle ? (
            <select
              data-testid="edit-order-sample-configuration"
              className="border border-gray-400 hover:border-gray-500 rounded-lg px-4 py-0.5 sm:py-1 mt-2 bg-font-contrast text-font-default focus:outline-primary outline-offset-2 outline-1 w-full"
              value={isPrimersRemovedSampleConfiguration(sampleConfiguration) ? 'true' : 'false'}
              disabled={!canSwitchSampleConfiguration}
              onChange={(e) => setSampleConfiguration(getMergedSampleConfigurationForPrimersRemoved(e.target.value === 'true'))}
            >
              <option value="false">No — primers are still present</option>
              <option value="true">Yes — primers already removed</option>
            </select>
          ) : (
            <select
              data-testid="edit-order-sample-configuration"
              className="border border-gray-400 hover:border-gray-500 rounded-lg px-4 py-0.5 sm:py-1 mt-2 bg-font-contrast text-font-default focus:outline-primary outline-offset-2 outline-1 w-full"
              value={sampleConfiguration}
              disabled={!canSwitchSampleConfiguration}
              onChange={(e) => setSampleConfiguration(e.target.value as SampleConfigurationTypeKey)}
            >
              {sampleFormatOptions.map((key) => (
                <option
                  key={key}
                  value={key}
                  disabled={blockedByPairs(key)}
                  title={describeSampleConfiguration(key, effectiveSequencingPlatform)}
                >
                  {getSampleConfigurationLabel(key, effectiveSequencingPlatform)}
                  {blockedByPairs(key) ? ' (not while samples are R1/R2 pairs)' : ''}
                </option>
              ))}
            </select>
          )}
          <p className="mt-1 text-xs text-base-content/70" data-testid="edit-order-sample-configuration-description">
            {describeSampleConfiguration(sampleConfiguration, effectiveSequencingPlatform)}
          </p>
          {sampleConfiguration !== order.sampleConfiguration && (
            <p className="mt-2 text-sm text-warning">
              All samples in this order will be re-labelled as{' '}
              <b>{getSampleConfigurationLabel(sampleConfiguration, effectiveSequencingPlatform)}</b>.
              {leavesR2Missing &&
                ` Each of the ${items.length} sample${items.length === 1 ? '' : 's'} then misses its R2 file: add them in the pool ` +
                  '(drop the R2 files, "Add R2" in a row, or "Pair R2 files by name") before submitting.'}
            </p>
          )}
          {!isPrimersRemovedToggle && !currentIsMerged && pairedItemCount > 0 && switchable && !preprocessingStarted && (
            <p className="mt-2 text-sm text-base-content/70" data-testid="edit-order-merged-blocked">
              {`Merged formats are unavailable while ${pairedItemCount} ${pairedItemCount === 1 ? 'sample is an R1/R2 pair' : 'samples are R1/R2 pairs'}. ` +
                'Delete those samples first, or use a new order.'}
            </p>
          )}
        </FormControl>

        <FormControl>
          <Label valid={true}>Processing type</Label>
          <select
            className="border border-gray-400 hover:border-gray-500 rounded-lg px-4 py-0.5 sm:py-1 mt-2 bg-font-contrast text-font-default focus:outline-primary outline-offset-2 outline-1 w-full"
            value={processingType}
            disabled={!!order.orderPostprocessingStarted}
            onChange={(e) => setProcessingType(e.target.value as ProcessingTypeKey)}
          >
            {Object.values(ProcessingTypes).map(({ key, label }) => (
              <option key={key} value={key} disabled={!isProcessingTypeAllowedForPlatform(key, effectiveSequencingPlatform)}>
                {label}
              </option>
            ))}
          </select>
          {platformProcessingTypeError && (
            <p className="mt-2 text-sm text-error" data-testid="edit-order-platform-processing-type-error">
              {platformProcessingTypeError}
            </p>
          )}
        </FormControl>
        <FormControl>
          <Label valid={true}>Target taxon</Label>
          <select
            className="border border-gray-400 hover:border-gray-500 rounded-lg px-4 py-0.5 sm:py-1 mt-2 bg-font-contrast text-font-default focus:outline-primary outline-offset-2 outline-1 w-full"
            value={selectedTaxa?.value || ''}
            disabled={!!order.orderSubmission}
            data-testid="create-order-taxa-select"
            onChange={(e) => {
              const found = taxaOptions.find((t) => t.value === e.target.value)
              setSelectedTaxa(found || taxaOptions[0])
            }}
          >
            {taxaOptions.map((taxon) => (
              <option
                key={taxon.value}
                value={taxon.value}
                disabled={taxon.disabled || !isProcessingTypeTaxaCombinationValid(processingType, taxon.value)}
              >
                {taxon.label}
              </option>
            ))}
          </select>
        </FormControl>
        {!isProcessingTypeTaxaCombinationValid(processingType, selectedTaxa.value) && (
          <p className="mt-2 text-sm text-error">{getProcessingTypeTaxaValidationMessage(processingType)}</p>
        )}
        {selectedTaxa?.value === 'CO1_ANIMAL' && (
          <FormControl>
            <Label valid={true}>Target region</Label>
            <select
              className="border border-gray-400 hover:border-gray-500 rounded-lg px-4 py-0.5 sm:py-1 mt-2 bg-font-contrast text-font-default focus:outline-primary outline-offset-2 outline-1 w-full"
              value={selectedRegion || ''}
              disabled={!!order.orderSubmission}
              onChange={(e) => setSelectedRegion(e.target.value || null)}
            >
              {Object.values(TargetRegions).map((r) => (
                <option key={r.key ?? 'null'} value={r.key ?? ''}>
                  {r.label}
                </option>
              ))}
            </select>
          </FormControl>
        )}
        <FormControl>
          <TooltipTrigger
            tooltip={
              'By default, spcfy removes singleton matches (i.e. species with only one read) from your results. If you are looking for invasive species, keeping singleton matches might be more desirable.'
            }
            withIcon
          >
            <Label inline valid={true}>
              Should Singleton Matches be present in the results?
            </Label>
          </TooltipTrigger>
          <select
            className="border border-gray-400 hover:border-gray-500 rounded-lg px-4 py-0.5 sm:py-1 mt-2 bg-font-contrast text-font-default focus:outline-primary outline-offset-2 outline-1 w-full"
            value={allowSingletons === null ? '' : allowSingletons ? 'true' : 'false'}
            disabled={!!order.orderSubmission}
            onChange={(e) => {
              const val = e.target.value
              setAllowSingletons(val === '' ? false : val === 'true')
            }}
          >
            <option value="false">No</option>
            <option value="true">Yes</option>
          </select>
        </FormControl>

        {selectedTaxa?.value === 'CO1_ANIMAL' && (
          <FormControl>
            <TooltipTrigger tooltip={'Redlist mode for this order.'} withIcon>
              <Label inline valid={true}>
                Redlist Mode
              </Label>
            </TooltipTrigger>
            <select
              className="border border-gray-400 hover:border-gray-500 rounded-lg px-4 py-0.5 sm:py-1 mt-2 bg-font-contrast text-font-default focus:outline-primary outline-offset-2 outline-1 w-full"
              value={redlistMode}
              onChange={(e) => setRedlistMode(e.target.value as RedlistModeKey)}
            >
              {Object.entries(RedlistModes).map(([key, { label }]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </FormControl>
        )}
        <div className="modal-action">
          <button
            className="btn btn-ghost mr-2"
            onClick={() => {
              resetForm()
              onClose()
            }}
          >
            Cancel
          </button>
          <button
            className={`btn btn-primary`}
            onClick={handleSubmit}
            disabled={
              !name.trim() || !isProcessingTypeTaxaCombinationValid(processingType, selectedTaxa.value) || !!platformProcessingTypeError
            }
          >
            Save Changes
          </button>
        </div>
      </div>
    </BaseModal>
  )
}
